import React, { ReactElement, useEffect } from 'react';
import { SiteDto } from '../../../api/ClientApi';
import { Box, DialogContent } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import {
    addOrRemoveWarehouseOwner,
    clearSiteOwners,
    importAllSiteOwners,
    importSiteForSiteOwnerForm,
    saveSiteOwners,
} from '../../../redux/actions/siteOwnerFormActions';
import SiteOwnerDetails from '../SiteOwnerDetails';
import { SaveEntityStatus } from '../../../redux/reducers/common';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

type SiteOwnerFormProps = {
    site?: SiteDto;
    open: boolean;
    closeForm(): void;
};

export default function SiteOwnerForm(props: SiteOwnerFormProps): ReactElement {
    const { site, open, closeForm } = props;

    const allSiteOwners = useAppSelector((state) => state.siteOwners.allWarehouseOwners);
    const currentOwnersIds = useAppSelector((state) => state.siteOwners.currentWarehouseOwners);

    const saveStatus = useAppSelector((state) => state.siteOwners.saveStatus);

    const dispatch = useAppDispatch();

    const handleClose = () => {
        dispatch(clearSiteOwners());
        closeForm();
    };

    React.useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            handleClose();
        }
    }, [dispatch, saveStatus]);

    useEffect(() => {
        // get all users
        // get their roles
        // if role is WO and not already on site, show employee as available
        if (!!site && open) {
            dispatch(importSiteForSiteOwnerForm(site.id ?? 0));
            dispatch(importAllSiteOwners());
        }
    }, [dispatch, site, open]);

    const handleAddOrRemoveWarehouseOwner = (WOId: string) => {
        dispatch(addOrRemoveWarehouseOwner(WOId));
    };

    const submit = () => {
        dispatch(saveSiteOwners());
    };

    const filteredNewOwners = currentOwnersIds?.filter((coi) => {
        return !site?.warehouseOwnersAzObjIds?.includes(coi);
    });

    const filteredCurrOwners = site?.warehouseOwnersAzObjIds?.filter((coi) => {
        return !currentOwnersIds?.includes(coi);
    });

    const areOwnersChanged =
        ((!currentOwnersIds || currentOwnersIds.length === 0) &&
            !!site?.warehouseOwnersAzObjIds &&
            site?.warehouseOwnersAzObjIds?.length > 0) ||
        (!!filteredCurrOwners && filteredCurrOwners.length > 0) ||
        (!!filteredNewOwners && filteredNewOwners.length > 0);

    return (
        <ResponsiveDialog scroll="body" maxWidth="md" open={open} onClose={handleClose}>
            <FormDialogTitle>Uredi odgovorna lica</FormDialogTitle>
            <DialogContent
                dividers
                sx={{
                    minHeight: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'center',
                }}
            >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {allSiteOwners?.map((ownerId) => {
                        const selected = currentOwnersIds?.includes(ownerId);
                        return (
                            <SiteOwnerDetails
                                key={ownerId}
                                siteOwnerId={ownerId}
                                handleClickOwner={() => handleAddOrRemoveWarehouseOwner(ownerId)}
                                selected={selected}
                            />
                        );
                    })}
                </Box>

                <FormActions
                    onCancel={handleClose}
                    onSubmit={submit}
                    submitLabel="Sačuvaj"
                    disabled={!areOwnersChanged || saveStatus === SaveEntityStatus.Pending}
                    pending={saveStatus === SaveEntityStatus.Pending}
                    inGrid={false}
                />
            </DialogContent>
        </ResponsiveDialog>
    );
}
